import axios from "axios";

import { getAccessToken } from "../../../shared/auth/authSession";
import { httpClient } from "../../../shared/api/httpClient";
import { env } from "../../../shared/config/env";
import type { PaginatedResponse } from "../../../shared/api/pagination";
import type {
  SellerProductBulkAssignRequest,
  SellerProductBulkAssignResponse,
  SellerProductBulkDeleteRequest,
  SellerProductBulkDeleteResponse,
  SellerProductOverview,
  SellerProductPage,
  SellerProductResponse,
  RecommendSellerProductOverviewPage,
} from "../model/types";

export interface RecommendSellerProductAssignment {
  product_code: string;
  product_name: string;
}

export interface RecommendSellerProductsRequest {
  seller_name: string;
  products: RecommendSellerProductAssignment[];
}

const recommendClient = axios.create({
  baseURL: env.recommendApiUrl,
  timeout: env.apiTimeoutMs,
  headers: { Accept: "application/json" },
});

recommendClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

export const assignRecommendSellerProducts = async (
  sellerCode: string,
  payload: RecommendSellerProductsRequest,
): Promise<unknown> => {
  const response = await recommendClient.post(
    `/sellers/${sellerCode}/products`,
    payload,
  );
  return response.data;
};

export interface SellerProductQuery {
  page: number;
  page_size: number;
  search?: string;
  seller_id?: number;
}

export const fetchSellerProducts = async (
  query: SellerProductQuery,
): Promise<SellerProductPage> => {
  const response = await httpClient.get<
    PaginatedResponse<SellerProductResponse>
  >("/seller-products", { params: query });
  return response.data;
};

export const fetchSellerProductOverview = async (
  query: Omit<SellerProductQuery, "seller_id">,
): Promise<RecommendSellerProductOverviewPage> => {
  const response = await recommendClient.get<
    PaginatedResponse<RecommendSellerProductOverviewPage["items"][number]>
  >("/sellers/products/overview", { params: query });
  return {
    items: response.data.items,
    pagination: response.data.pagination,
  };
};

export const bulkAssignSellerProducts = async (
  payload: SellerProductBulkAssignRequest,
): Promise<SellerProductBulkAssignResponse> => {
  const response = await httpClient.post<SellerProductBulkAssignResponse>(
    "/seller-products/bulk",
    payload,
  );
  return response.data;
};

export const bulkDeleteSellerProducts = async (
  payload: SellerProductBulkDeleteRequest,
): Promise<SellerProductBulkDeleteResponse> => {
  const response = await httpClient.post<SellerProductBulkDeleteResponse>(
    "/seller-products/bulk-delete",
    payload,
  );
  return response.data;
};

export const setSellerProductLeader = async (
  productCode: string,
  sellerCode: string,
): Promise<unknown> => {
  const response = await recommendClient.put("/products/leader", {
    product_code: productCode,
    seller_code: sellerCode,
  });
  return response.data;
};

export const deleteSellerProduct = async (
  sellerProductId: number,
): Promise<void> => {
  await httpClient.delete(`/seller-products/${sellerProductId}`);
};
